import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import API from '../api';
import i18n from '../i18n';
import { useTranslation } from 'react-i18next';
import './styles.css';

export default function OrderPrint() {
  const { id } = useParams();
  const navigate = useNavigate();

  // local i18n instance so changing language here DOES NOT affect the global app
  const localI18n = useMemo(() => i18n.cloneInstance(), []);
  const { t } = useTranslation(null, { i18n: localI18n });

  const [order, setOrder] = useState(null);
  const [mode, setMode] = useState('thermal');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [ready, setReady] = useState(false); // wait until local language set
  const [barcodeSVG, setBarcodeSVG] = useState('');

  // helper: pick localized field from order (e.g. customer_name_ar / customer_name_en) or fallback
  const getLocalized = (base) => {
    if (!order) return '';
    const lang = (localI18n && localI18n.language) ? localI18n.language : 'en';
    const primary = `${base}_${lang}`;
    const short = lang.split('-')[0];
    const candidates = [primary, `${base}_${short}`, base];
    for (const k of candidates) {
      if (Object.prototype.hasOwnProperty.call(order, k) && order[k]) return order[k];
    }
    // nested object fallback (e.g. order.customer = { name: ... })
    const val = order[base];
    if (val && typeof val === 'object') return val.name || val.title || '';
    return val || '';
  };
  
  const barcodeSrc = `/api/orders/${id}/barcode_svg/`;

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        // read preferred language from backend and apply to local i18n
        let lang = 'en';
        try {
          const res = await API.get('profile/me/', { skipAuthRedirect: true });
          lang = res.data?.language || lang;
        } catch (e) {
          // if API fails, fallback to stored preferred_language (useful for print popups)
          const stored = localStorage.getItem('preferred_language');
          if (stored) lang = stored;
          else if (e?.response?.status !== 404) console.warn('profile/me error', e);
        }
        if (mounted) await localI18n.changeLanguage(lang);

        // then load order
        const r = await API.get(`orders/${id}/`);
        if (!mounted) return;
        setOrder(r.data);

        // fetch barcode SVG as text
        try {
          const svgRes = await API.get(`orders/${id}/barcode_svg/`, { responseType: 'text' });
          if (mounted && typeof svgRes.data === 'string' && svgRes.data.includes('<svg')) {
            // نظف أي فراغات أو أسطر قبل <svg>
            const svgStart = svgRes.data.indexOf('<svg');
            setBarcodeSVG(svgRes.data.slice(svgStart));
          } else {
            setBarcodeSVG('');
          }
        } catch (e) {
          setBarcodeSVG('');
        }
      } catch (err) {
        console.error('OrderPrint fetch error', err);
        if (mounted) setError(localI18n.t('Failed to load order') || 'Failed to load order');
      } finally {
        if (mounted) {
          setLoading(false);
          setReady(true);
        }
      }
    })();
    return () => { mounted = false; };
  }, [id, localI18n]);

  const handlePrint = () => setTimeout(() => window.print(), 200);
  const handleBack = () => { if (window.opener) try { window.close(); } catch(e){ navigate('/orders/search'); } else navigate(-1); };

  if (!ready) {
    // prevent flashing wrong language — show neutral loader while local language applied
    return <div className="page-container">{t ? t('Loading...') : 'Loading...'}</div>;
  }

  return (
    <div className={`page-container print-page ${localI18n.language === 'ar' ? 'rtl' : ''}`}>
      <div className="page-header">
        <h2>{t('Print Order')}</h2>
        <div className="header-actions">
          <button className="app-button outline" onClick={handleBack}>← {t('Back')}</button>
          <button className="app-button" onClick={() => navigate('/')}>{t('Home')}</button>
        </div>
      </div>

      {loading && <div className="muted">{t('Loading...')}</div>}
      {error && <div className="error">{error}</div>}

      {!loading && !error && order && (
        <div className="print-card">
          <div className="print-controls">
            <label className="radio-inline"><input type="radio" name="mode" value="thermal" checked={mode==='thermal'} onChange={()=>setMode('thermal')} /> <span>{t('Thermal 52mm')}</span></label>
            <label className="radio-inline" style={{marginLeft:12}}><input type="radio" name="mode" value="normal" checked={mode==='normal'} onChange={()=>setMode('normal')} /> <span>{t('Normal')}</span></label>
            <button className="app-button" onClick={handlePrint} style={{marginLeft:12}}>{t('Print')}</button>
          </div>

          <div id="print-area" className={`print-area ${mode==='thermal' ? 'thermal' : 'normal'}`} dir={localI18n.language === 'ar' ? 'rtl' : 'ltr'}>
            <div className="print-header" style={{textAlign:'center', marginBottom: 12}}>
              <strong className="print-title">{t('Order')} #{getLocalized('order_number') || order.id}</strong>
            </div>
            <div className="print-body" style={{display:'flex', flexDirection:'column', gap: '8px'}}>
              <div><strong>{t('Customer')}:</strong> {getLocalized('customer_name') || getLocalized('customer')}</div>
              <div><strong>{t('Date')}:</strong> {order.created_at ? new Intl.DateTimeFormat(localI18n.language, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(order.created_at)) : ''}</div>
              <div><strong>{t('Driver')}:</strong> {getLocalized('driver_name') || getLocalized('driver') || t('No driver')}</div>
              <div><strong>{t('Status')}:</strong> {t(`status.${order.status}`) || order.status}</div>
              <div><strong>{t('Pickup')}:</strong> {getLocalized('pickup_address') || getLocalized('origin')}</div>
              <div><strong>{t('Delivery')}:</strong> {getLocalized('delivery_address') || getLocalized('destination')}</div>
              <div
                className="barcode-wrap"
                style={{
                  textAlign: localI18n.language === 'ar' ? 'right' : 'left',
                  margin: '0',
                  padding: '0',
                  width: '100%',
                  overflow: 'visible',
                  direction: localI18n.language === 'ar' ? 'rtl' : 'ltr',
                  minHeight: '40px',
                  display: 'block',
                }}
              >
                {barcodeSVG ? (
                  <div
                    dangerouslySetInnerHTML={{ __html: barcodeSVG }}
                    style={{
                      maxWidth: '100%',
                      minHeight: '40px',
                      overflow: 'visible',
                      textAlign: localI18n.language === 'ar' ? 'right' : 'left',
                      direction: localI18n.language === 'ar' ? 'rtl' : 'ltr',
                      margin: localI18n.language === 'ar' ? '0 0 0 auto' : '0',
                      padding: '0',
                      display: 'block',
                    }}
                  />
                ) : (
                  <div className="muted" style={{marginTop:8}}>{t('Barcode not available')}</div>
                )}
              </div>
              {getLocalized('description') && <div className="print-desc">{getLocalized('description')}</div>}
              {getLocalized('notes') && <div className="print-notes">{getLocalized('notes')}</div>}
            </div>
          </div>

          <style>{`@media print {
            body * { visibility: hidden; }
            #print-area, #print-area * { visibility: visible; }
            #print-area {
              margin: 0 !important;
              box-shadow: none !important;
              width: 100vw !important;
              min-width: 0 !important;
              display: flex !important;
              flex-direction: column !important;
              align-items: stretch !important;
            }
            .print-area.thermal {
              font-size: 10px !important;
              line-height: 1.2 !important;
              width: 52mm !important;
              max-width: 52mm !important;
              padding: 0 !important;
              display: flex !important;
              flex-direction: column !important;
              align-items: stretch !important;
            }
            .print-title, .print-header, .print-body, .barcode-wrap {
              font-size: 10px !important;
              line-height: 1.2 !important;
              margin: 0 !important;
              padding: 0 !important;
              display: block !important;
              width: 100% !important;
              text-align: unset !important;
            }
            .barcode-wrap {
              margin: 0 !important;
              padding: 0 !important;
              overflow: visible !important;
              width: 100% !important;
              display: block !important;
              text-align: unset !important;
            }
            .barcode-wrap svg {
              display: block !important;
              margin: 0 !important;
              width: 100% !important;
              max-width: 100% !important;
              min-width: 100% !important;
              height: auto !important;
              min-height: 40px !important;
              overflow: visible !important;
              box-sizing: border-box !important;
            }
            .print-area.thermal[dir="rtl"] .barcode-wrap,
            .print-area.thermal[dir="rtl"] .barcode-wrap svg {
              text-align: right !important;
            }
            .print-area.thermal[dir="ltr"] .barcode-wrap,
            .print-area.thermal[dir="ltr"] .barcode-wrap svg {
              text-align: left !important;
            }
            button, input, label { display: none !important; }
            @page { size: 52mm; margin: 2mm; }
          }`}</style>
        </div>
      )}
    </div>
  );
}